/**
 * Public landing page.
 *
 * Its job is to make a citizen willing to report. That means being concrete
 * about three things: what water theft does to other farmers, what the law
 * actually says, and — most importantly — exactly what the Department will and
 * will not know about the person reporting.
 *
 * The anonymity section is not marketing. Every claim on it is enforced
 * somewhere in this repository, and it names where.
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export function Landing() {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-8">
      <section>
        <h1 className="text-2xl font-semibold sm:text-3xl">{t('landing.heading')}</h1>
        <p className="mt-3 text-lg" style={{ color: 'var(--text-secondary)' }}>
          {t('landing.intro')}
        </p>
        <Link
          to="/report"
          className="mt-6 inline-block rounded-md px-5 py-3 font-medium"
          style={{ background: 'var(--accent)', color: '#fff' }}
        >
          {t('landing.cta')}
        </Link>
      </section>

      {/* The privacy promise sits high on the page, above the explanation of the
          problem. Someone deciding whether it is safe to report needs this
          before they need to be convinced the problem matters. */}
      <section
        className="rounded-lg p-5"
        style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}
      >
        <h2 className="text-xl font-semibold">{t('landing.privacy.heading')}</h2>
        <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
          {t('landing.privacy.intro')}
        </p>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <h3 className="text-sm font-semibold" style={{ color: 'var(--success)' }}>
              {t('landing.privacy.weSeeHeading')}
            </h3>
            <ul className="mt-2 space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
              <li>{t('landing.privacy.weSee1')}</li>
              <li>{t('landing.privacy.weSee2')}</li>
              <li>{t('landing.privacy.weSee3')}</li>
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-semibold" style={{ color: 'var(--danger)' }}>
              {t('landing.privacy.weDontHeading')}
            </h3>
            <ul className="mt-2 space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
              <li>{t('landing.privacy.weDont1')}</li>
              <li>{t('landing.privacy.weDont2')}</li>
              <li>{t('landing.privacy.weDont3')}</li>
            </ul>
          </div>
        </div>

        <p className="mt-4 text-sm" style={{ color: 'var(--text-muted)' }}>
          {t('landing.privacy.footnote')}
        </p>
      </section>

      <section className="grid gap-5 sm:grid-cols-2">
        <InfoCard title={t('landing.impact.heading')} body={t('landing.impact.body')} />
        <InfoCard title={t('landing.law.heading')} body={t('landing.law.body')} />
      </section>

      <section>
        <h2 className="text-xl font-semibold">{t('landing.how.heading')}</h2>
        <ol className="mt-4 grid gap-4 sm:grid-cols-3">
          <Step n={1} title={t('landing.how.step1Title')} body={t('landing.how.step1Body')} />
          <Step n={2} title={t('landing.how.step2Title')} body={t('landing.how.step2Body')} />
          <Step n={3} title={t('landing.how.step3Title')} body={t('landing.how.step3Body')} />
        </ol>
      </section>

      <section
        className="rounded-lg p-5"
        style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}
      >
        <h2 className="text-lg font-semibold">{t('landing.safety.heading')}</h2>
        <p className="mt-2" style={{ color: 'var(--text-secondary)' }}>
          {t('landing.safety.body')}
        </p>
      </section>
    </div>
  );
}

function InfoCard({ title, body }: { title: string; body: string }) {
  return (
    <div
      className="rounded-lg p-5"
      style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}
    >
      <h2 className="text-lg font-semibold">{title}</h2>
      <p className="mt-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
        {body}
      </p>
    </div>
  );
}

function Step({ n, title, body }: { n: number; title: string; body: string }) {
  return (
    <li
      className="rounded-lg p-4"
      style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}
    >
      <span
        className="inline-flex h-7 w-7 items-center justify-center rounded-full text-sm font-semibold"
        style={{ background: 'var(--accent)', color: '#fff' }}
        aria-hidden="true"
      >
        {n}
      </span>
      <h3 className="mt-2 font-medium">{title}</h3>
      <p className="mt-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
        {body}
      </p>
    </li>
  );
}
